import { useState, useEffect } from "react";
import css from "../css";

export default function LoginPage({ onLogin, onRegister, onGoogle }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [birthday, setBirthday] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let attempts = 0;
    const init = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: async (response) => {
            setLoading(true); setError("");
            try { await onGoogle(response.credential); }
            catch (e) { setError(e.error || "Google sign-in failed."); }
            finally { setLoading(false); }
          },
        });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-btn"),
          { theme: "filled_black", size: "large", width: 328, text: "continue_with" }
        );
      } else if (attempts < 20) {
        attempts++;
        setTimeout(init, 250);
      }
    };
    init();
  }, [onGoogle]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = async () => {
    if (!email.trim() || !password.trim()) { setError("Please fill in all fields."); return; }
    if (mode === "register" && (!firstName.trim() || !lastName.trim())) { setError("Please enter your first and last name."); return; }
    if (mode === "register" && !gender) { setError("Please select your gender."); return; }
    if (mode === "register" && !birthday) { setError("Please enter your birthday."); return; }
    setLoading(true); setError("");
    try {
      if (mode === "login") await onLogin(email.trim(), password);
      else await onRegister(email.trim(), password, `${firstName.trim()} ${lastName.trim()}`, adminKey || undefined, gender, birthday);
    } catch (e) {
      if (e.error === "admin_key_required") {
        setShowAdminKey(true);
        setError("This email requires an admin key to register.");
      } else {
        setError(e.error || "Something went wrong.");
      }
    } finally { setLoading(false); }
  };

  return (
    <>
      <style>{css}</style>
      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">
            <div className="login-logo-mark">SILVER<span>BACK</span></div>
            <div className="login-logo-sub">PRIMAL PERFORMANCE TRACKER</div>
          </div>
          <div className="login-title">{mode === "login" ? "Sign In" : "Create Account"}</div>
          {error && <div className="login-error">{error}</div>}
          <div id="google-signin-btn" style={{marginBottom:16,display:"flex",justifyContent:"center"}}/>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <div style={{flex:1,height:1,background:"var(--border2)"}}/>
            <span style={{fontSize:10,color:"var(--muted)",letterSpacing:"0.08em"}}>OR</span>
            <div style={{flex:1,height:1,background:"var(--border2)"}}/>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {mode === "register" && (
              <>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">First Name</label>
                    <input className="input" placeholder="John" value={firstName}
                      onChange={e=>{setFirstName(e.target.value);setError("");}}
                      onKeyDown={e=>e.key==="Enter"&&handle()} autoFocus/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name</label>
                    <input className="input" placeholder="Smith" value={lastName}
                      onChange={e=>{setLastName(e.target.value);setError("");}}
                      onKeyDown={e=>e.key==="Enter"&&handle()}/>
                  </div>
                </div>
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Gender <span className="req-star">*</span></label>
                    <select className="select" value={gender} onChange={e=>{setGender(e.target.value);setError("");}}>
                      <option value="">Select...</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Birthday <span className="req-star">*</span></label>
                    <input className="input" type="date" value={birthday}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={e=>{setBirthday(e.target.value);setError("");}}/>
                  </div>
                </div>
              </>
            )}
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" type="email" placeholder="you@example.com" value={email}
                onChange={e=>{setEmail(e.target.value);setError("");setShowAdminKey(false);}}
                onKeyDown={e=>e.key==="Enter"&&handle()}
                autoFocus={mode==="login"} autoComplete="email"/>
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="input" type="password" placeholder="Enter password" value={password}
                onChange={e=>{setPassword(e.target.value);setError("");}}
                onKeyDown={e=>e.key==="Enter"&&handle()}
                autoComplete={mode==="login"?"current-password":"new-password"}/>
            </div>
            {showAdminKey && (
              <div className="form-group">
                <label className="form-label" style={{color:"var(--accent)"}}>Admin Key</label>
                <input className="input" type="password" placeholder="Enter admin key" value={adminKey}
                  onChange={e=>{setAdminKey(e.target.value);setError("");}}
                  onKeyDown={e=>e.key==="Enter"&&handle()} autoFocus/>
              </div>
            )}
            <button className="btn btn-primary"
              style={{width:"100%",justifyContent:"center",marginTop:4}}
              onClick={handle} disabled={loading}>
              {loading?"Please wait...":mode==="login"?"Sign In":"Create Account"}
            </button>
          </div>
          <div className="login-toggle">
            {mode==="login"?"Don't have an account? ":"Already have an account? "}
            <span onClick={()=>{setMode(mode==="login"?"register":"login");setError("");setShowAdminKey(false);setGender("");setBirthday("");}}>
              {mode==="login"?"Register":"Sign In"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
